import { AxDropdown } from "@axess/limestone";
import { AxInit, ModuleName } from "../../../Lib/AxSystem/AxInit";
import { AxLoad } from "../../../Lib/AxSystem/AxLoad";
import { LoadingSpinnerPositionEnum } from "../../../Lib/Enums/LoadingSpinnerPositionEnum";
import { MeasurementHelper } from "../../../Lib/Helper/MeasurementHelper";
import { addUniqueObjectToArray } from "../../../Lib/Helper/ObjectHelper";
import { AxLoading } from "../../../Limestone/AxLoading/AxLoading";
import { CustomerLessonProperties, InitCustomerProps } from "../../Account/CustomerLessonProperties";
import { ComplexPackageBookingOptions } from "../ComplexPackageBookingOptions";

@ModuleName("CompPackBookingPersonOptions")
export class CompPackBookingPersonOptions implements AxInit {
    private axLoading: AxLoading = new AxLoading();
    private initData!: initData;
    private $parent: JQuery<HTMLElement> = $('[data-frontoffice-view="complexpackagesbooking"]');

    public cpBookingOptions: ComplexPackageBookingOptions = AxLoad.GetModule<ComplexPackageBookingOptions>("ComplexPackageBookingOptions");

    public genderDropdown: AxDropdown = new AxDropdown();
    public heightDropdown: AxDropdown = new AxDropdown();
    public weightDropdown: AxDropdown = new AxDropdown();
    public shoeSizeDropdown: AxDropdown = new AxDropdown();

    public customerLessonProps: CustomerLessonProperties = new CustomerLessonProperties();
    public customerLesson: CustomerLessonProperties[] = [];

    private measurementHelper: MeasurementHelper = new MeasurementHelper();
    
    AxInit(init: initData): void {
        this.initData = init;

        [... Array(init.participants)].forEach((_, i: number) => {
            const participant = i + 1;
            const dropdown = new AxDropdown();
            dropdown.init(`.prod_selection__person-selection.participant-${participant} .ax-dropdown`, {
                multiSelect: false,
                searchBar: false,
                onSelect: (_, value: string | string[]) => {
                    this.loadPersonPartialAfterSelect(value as string, participant);
                    this.$parent.find(`#personOptionsHolder-${participant}`).addClass('d-none');
                }
            });
            dropdown.resetSelection();
            this.cpBookingOptions.dropDowns = addUniqueObjectToArray(this.cpBookingOptions.dropDowns, dropdown, 'selector');
        });
    }

    setLessonPropsToBookingOptions(participant: number = 1) {
        const index = participant - 1;
        this.cpBookingOptions.bookingValuesList[index].lessonAllergies = this.customerLessonProps.allergies;
        this.cpBookingOptions.bookingValuesList[index].lessonLanguages = this.customerLessonProps.languages;
        this.cpBookingOptions.bookingValuesList[index].lessonMedicals = this.customerLessonProps.medicalStates;
        this.cpBookingOptions.bookingValuesList[index].lessonLevels = this.customerLessonProps.lessonLevels;
    }

    loadPersonPartialAfterSelect(value: string, participant: number = 1) {
        const index = participant - 1;
        const accountId = Number(value);
        this.cpBookingOptions.bookingValuesList[index].accountId = accountId;

        this.loadPersonPartial(accountId, participant);
    }

    loadPersonPartial(accountNr: number, participant: number = 1, unitInt: number | undefined = undefined) {
        const $container = this.$parent.find(`[data-person-options-holder="${participant}"]`)
        const data = {
            accountNr,
            unitInt,
            participant,
        };
        $container.removeClass('d-none');
        this.axLoading.ShowLoadingIndicatorForMultible(
            [this.$parent.find(`[data-person-options-holder="${participant}"]`)?.get(0) as HTMLElement],
            true,
            LoadingSpinnerPositionEnum.Center
        );

        AxessViewer.loadPartialView('/ComplexPackagesBooking/GetPersonOptionsPartial', data, (res: { success: boolean, view: string }) => {
            if (res.success) {
                $container.html(res.view);
                this.customerLessonProps.AxInit(new InitCustomerProps(`#lessonPropsForm-${participant}`, ''));
                this.initPersonDataPartial(participant);
            } else {
                this.axLoading.HideLoadingIndicator(this.$parent.find(`[data-person-options-holder="${participant}"]`)?.get(0) as HTMLElement);
            }
        }, {
            silent: true,
        });
    }

    reloadPersonPartial(participant: number = 1, unitInt: number) {
        const index = participant - 1;
        this.loadPersonPartial(this.cpBookingOptions.bookingValuesList[index].accountId, participant, unitInt);
    }

    initPersonDataPartial(participant: number = 1) {
        const index = participant - 1;
        const bookingValues = this.cpBookingOptions.bookingValuesList[index];
        const $parentPerson = this.$parent.find(`[data-person-options-holder="${participant}"]`);

        $parentPerson.off('change').on('change', `input[type=radio][name="DataUnitSystem-${participant}"]`, (e) => {
            const unitInt = Number($(e.currentTarget).val() as string);
            this.reloadPersonPartial(participant, unitInt);
        });

        this.genderDropdown.init(`.prod_selection__gender-selection.participant-${participant} .ax-dropdown`, {
            multiSelect: false,
            searchBar: false,
            onSelect: (_, value: string | string []) => {
                bookingValues.gender = value as string;
            }
        });
        this.cpBookingOptions.dropDowns = addUniqueObjectToArray(this.cpBookingOptions.dropDowns, this.genderDropdown, 'selector');

        if (this.initData.isTextInput) {
            $parentPerson.on('keyup', `[name="height-${participant}"]`, (e) => {
                bookingValues.height = Number($(e.target).val() || null);
            });

            $parentPerson.on('keyup', `[name="heightFeet-${participant}"], [name="heightInch-${participant}"]`, (e) => {
                const feet = Number($(`[name="heightFeet-${participant}"]`).val());
                const inches = Number($(`[name="heightInch-${participant}"]`).val());

                const totalInches = this.measurementHelper.feetAndInchesToTotalInches(feet, inches);
                bookingValues.height = this.measurementHelper.inchesToMeters(totalInches);
            });

            $parentPerson.on('keyup', `[name="weight-${participant}"]`, (e) => {
                const rawWeight = Number($(e.target).val());
                const isImperial = $parentPerson.find(`[name="DataUnitSystem-${participant}"]:checked`).val() === '1';

                bookingValues.weight = isImperial 
                    ? this.measurementHelper.lbsToKg(rawWeight) 
                    : rawWeight;
            });

            $parentPerson.find(`[name="height-${participant}"], [name="heightFeet-${participant}"], [name="heightInch-${participant}"], [name="weight-${participant}"]`).trigger('keyup');
        } else {
            this.heightDropdown.init(`.prod_selection__height-selection.participant-${participant} .ax-dropdown`, {
                multiSelect: false,
                searchBar: false,
                onSelect: (_, value) => {
                    bookingValues.height = Number(value);
                }
            });
            this.cpBookingOptions.dropDowns = addUniqueObjectToArray(this.cpBookingOptions.dropDowns, this.heightDropdown, 'selector');

            this.weightDropdown.init(`.prod_selection__weight-selection.participant-${participant} .ax-dropdown`, {
                multiSelect: false,
                searchBar: false,
                onSelect: (_, value) => {
                    bookingValues.weight = Number(value);
                }
            });
            this.cpBookingOptions.dropDowns = addUniqueObjectToArray(this.cpBookingOptions.dropDowns, this.weightDropdown, 'selector');
        }

        this.shoeSizeDropdown.init(`.prod_selection__shoeSize-selection.participant-${participant} .ax-dropdown`, {
            multiSelect: false,
            searchBar: false,
            onSelect: (_, value) => {
                bookingValues.shoeSize = Number(value);
            }
        });
        this.cpBookingOptions.dropDowns = addUniqueObjectToArray(this.cpBookingOptions.dropDowns, this.shoeSizeDropdown, 'selector');

        const userSkillLevel = $(`#compPackUserSkillLevel-${participant}`).val();
        if (userSkillLevel != 0) {
            bookingValues.skillLevel = Number(userSkillLevel);
            const $selected = $parentPerson.find(`[data-skillLevelId="${userSkillLevel}"]`);
            this.toggleQuestionChip($selected);
        }

        $parentPerson.off('click').on('click', '[data-skillLevelId]', (e) => {
            const $target = $(e.currentTarget);

            $target.siblings('.active').each((i, e) => {
                this.toggleQuestionChip($(e));
            });

            setTimeout(() => this.toggleQuestionChip($target));

            const skillLevelId = $target.attr('data-skillLevelId');
            bookingValues.skillLevel = Number(skillLevelId);
        });

        this.setLessonPropsToBookingOptions(participant);
    }

    toggleQuestionChip($target: JQuery<HTMLElement>) {
        $target.toggleClass('active ax-fill-primary');
        $target.find('i.las').toggleClass('ax-color-primary');
        $target.find('i.lar').toggleClass('ax-color-primary--light');
    }    

    setDescriptionAndGenerateHiddenFieldForLessonProperties(formName : string, participant: number = 1) {
        this.customerLessonProps.setDescriptionAndGenerateHiddenFieldForLessonProperties(formName);
        this.setLessonPropsToBookingOptions(participant);
    }
}

export interface initData {
    isTextInput: boolean;
    participants: number;
}
